import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Globe, Link2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { SetupStepper } from './SetupStepper';
import { WriteEnablePanel } from '@/components/report/WriteEnablePanel';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { AccountManager } from '../connection/AccountManager';
import { useAuthStore } from '@/stores/authStore';
import { useSetupStore } from '@/stores/setupStore';
import { connection } from '@/lib/api';
import { BrowserPanel } from '../browser/BrowserPanel';
import { useChatStore } from '@/stores/chatStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useBrowserStore } from '@/stores/browserStore';
import { useBillingStore } from '@/stores/billingStore';
import { useZoomLoginDetect } from '@/hooks/useZoomLoginDetect';
import { cn } from '@/lib/utils';

function StatusBar({ showZoomChip }: { showZoomChip?: boolean }) {
  const connected = useBrowserStore((s) => s.harnessConnected);
  const zoomConnected = useConnectionStore((s) => s.isConnected);
  return (
    <header className="flex items-center justify-end gap-2 px-4 py-3">
      {/* Consolidated status row: in browser mode the Zoom API chip lives here
          (the composer's interactive one is hidden) so all status reads in one
          place instead of opposite corners. */}
      {showZoomChip && (
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs',
            zoomConnected ? 'bg-green-900/30 text-green-400' : 'bg-white/5 text-gray-400'
          )}
        >
          {zoomConnected ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
          {zoomConnected ? 'Zoom API Connected' : 'Zoom API Offline'}
        </div>
      )}
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs',
          connected
            ? 'bg-green-900/30 text-green-400'
            : 'bg-white/5 text-gray-400'
        )}
      >
        {connected ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <Globe className="h-3.5 w-3.5" />
        )}
        {connected ? 'Browser Harness Connected' : 'Browser Harness Offline'}
      </div>
    </header>
  );
}

interface WelcomeAction {
  label: string;
  type: 'connect-zoom' | 'send' | 'navigate';
  value?: string;
  primary?: boolean;
}

interface WelcomeState {
  message: string;
  actions: WelcomeAction[];
}

function useWelcomeState(): WelcomeState {
  const user = useAuthStore((s) => s.user);
  const hasZoom = useAuthStore((s) => s.hasZoomConnection);
  const isPro = useBillingStore((s) => s.isPro);

  return useMemo(() => {
    const name = user?.name?.split(' ')[0];
    const greeting = name ? `Hey ${name}!` : 'Hey there!';

    if (isPro()) {
      return {
        message: `${greeting} What can I help with today?\n\nI can review your Zoom setup, configure settings, troubleshoot issues, or answer questions. Just ask.`,
        actions: [
          { label: 'Review my Zoom setup', type: 'send', value: 'Review my Zoom setup' },
          { label: 'Check for issues', type: 'send', value: 'Check my Zoom configuration for issues' },
        ],
      };
    }

    if (hasZoom) {
      const scoreNote = "I've reviewed your Zoom setup and found a few things worth looking at.";
      return {
        message: `${greeting} ${scoreNote}\n\nI can walk you through the findings and help you understand what to prioritize. With a **Pro upgrade**, I can apply fixes directly — no manual work needed.`,
        actions: [
          { label: 'Review audit results', type: 'send', value: 'Review my audit results', primary: true },
          { label: 'Upgrade to Pro', type: 'navigate', value: '/settings?tab=billing' },
        ],
      };
    }

    return {
      message: `${greeting} I'm Zac — your AI assistant for Zoom.\n\nI can answer questions about any Zoom product. But to really help, I need to see your setup. **Connect your Zoom account** and I'll run a free audit — I'll review your configuration, spot issues, and show you exactly what to fix.\n\nI use read-only OAuth access. I never see your password, and you can disconnect anytime.`,
      actions: [
        { label: 'Connect Zoom', type: 'connect-zoom', primary: true },
        { label: 'What can you do?', type: 'send', value: 'What can you help me with?' },
      ],
    };
  }, [user?.name, hasZoom, isPro]);
}

interface WelcomeMessageProps {
  compact?: boolean;
  onConnectZoom: () => void;
  onSendMessage: (text: string) => void;
  onNavigate: (path: string) => void;
}

function WelcomeMessage({ compact, onConnectZoom, onSendMessage, onNavigate }: WelcomeMessageProps) {
  const { message, actions } = useWelcomeState();

  const handleAction = useCallback((action: WelcomeAction) => {
    switch (action.type) {
      case 'connect-zoom': return onConnectZoom();
      case 'send': return onSendMessage(action.value!);
      case 'navigate': return onNavigate(action.value!);
    }
  }, [onConnectZoom, onSendMessage, onNavigate]);

  return (
    <div className={`flex flex-col gap-2 py-1.5 ${compact ? 'px-2' : 'max-w-2xl w-full'}`}>
      <div className="flex items-end gap-2 justify-start">
        <video
          src="/zac-avatar.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="h-9 w-9 shrink-0 rounded-lg object-cover"
          aria-label="ZAC"
        />
        <div className="max-w-[85%] px-4 py-3 text-foreground rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-sm bg-white/[0.04]">
          <div className="prose dark:prose-invert max-w-none text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message}</ReactMarkdown>
          </div>
        </div>
      </div>
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2 ml-11">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => handleAction(action)}
              className={cn(
                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                action.primary
                  ? 'bg-blue-600 text-white hover:bg-blue-500'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10',
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CHAT_SIDEBAR_WIDTH = '380px';

export function ChatContainer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showAccountManager, setShowAccountManager] = useState(false);
  // Watch the guided-setup browser for a completed Zoom sign-in -> ZAC takes over.
  useZoomLoginDetect();
  // While ZAC is awaiting the app name, lock the main composer so the user
  // answers via the in-chat suggested-name input rather than free chat.
  const awaitingAppName = useSetupStore((s) => s.awaitingAppName);
  const setupConversationId = useSetupStore((s) => s.setupConversationId);
  const agentControl = useBrowserStore((s) => s.agentControl);
  const userPaused = useBrowserStore((s) => s.userPaused);
  const leasedBy = useBrowserStore((s) => s.leasedBy);
  const {
    messages,
    currentConversationId,
    isStreaming,
    streamingContent,
    streamingArtifacts,
    profilingProgress,
    thinkingStatus,
    sendMessage,
  } = useChatStore();
  const { isConnected, isLoading: isConnectionLoading, checkConnection, disconnect } = useConnectionStore();
  const { panelOpen: browserOpen, closePanel: closeBrowserPanel } = useBrowserStore();

  // Handle OAuth return — refresh connection state and clear query params
  useEffect(() => {
    const oauthResult = searchParams.get('oauth');
    if (oauthResult) {
      setSearchParams({}, { replace: true });
      checkConnection();
    }
  }, [searchParams, setSearchParams, checkConnection]);

  // Check connection state on mount. We no longer auto-connect with default
  // credentials — under the guided flow the user provisions their own
  // Server-to-Server app, so there is no shared-default connect path.
  useEffect(() => {
    checkConnection();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const handleSendMessage = async (content: string) => {
    await sendMessage(content);
  };

  const handleDisconnect = useCallback(async () => {
    if (confirm('Are you sure you want to disconnect this Zoom account?')) {
      await disconnect();
    }
  }, [disconnect]);

  const handleConnectZoom = useCallback(async () => {
    try {
      const { url } = await connection.getZoomConnectUrl({ purpose: 'reader', returnTo: '/chat' });
      window.location.href = url;
    } catch (err) {
      console.error('[ConnectZoom] failed to get OAuth URL:', err);
    }
  }, []);

  const handleAction = useCallback((action: string) => {
    switch (action) {
      case 'connect-zoom':
      case 'add-account':
        handleConnectZoom();
        break;
      case 'disconnect-zoom':
        handleDisconnect();
        break;
      case 'manage-accounts':
        setShowAccountManager(true);
        break;
    }
  }, [handleConnectZoom, handleDisconnect]);

  // The guided-setup chat gets a pinned progress stepper and a leaner composer
  // (no browser toggle — the panel is forced open; no duplicate Zoom chip).
  const inSetupChat = !!currentConversationId && currentConversationId === setupConversationId;
  const writeEnablePhase = useWriteEnableStore((s) => s.phase);

  const chatInput = (
    <MessageInput
      onSend={handleSendMessage}
      onAction={handleAction}
      disabled={isStreaming || awaitingAppName}
      isConnected={isConnected}
      isConnecting={isConnectionLoading}
      hideConnectionChip={browserOpen}
      hideBrowserToggle={inSetupChat}
      placeholderOverride={leasedBy || (agentControl && !userPaused) ? 'ZAC is driving — take over anytime' : undefined}
      onAttach={inSetupChat ? undefined : (file) => void useChatStore.getState().attachDocument(file)}
    />
  );

  // =========================================================================
  // Welcome screen (no conversation yet)
  // =========================================================================
  if (!currentConversationId && messages.length === 0) {
    // Browser mode: browser full screen, chat in right sidebar
    if (browserOpen) {
      return (
        <>
          <div className="flex h-full">
            {/* Browser fills main area */}
            <div className="flex-1 min-w-0">
              <BrowserPanel onClose={closeBrowserPanel} />
            </div>

            {/* Chat sidebar */}
            <div
              className="hidden lg:flex flex-col border-l border-border bg-background"
              style={{ width: CHAT_SIDEBAR_WIDTH, minWidth: CHAT_SIDEBAR_WIDTH }}
            >
              <StatusBar showZoomChip />
              <div className="flex flex-col items-start p-4 flex-1 min-h-0 overflow-y-auto">
                <WelcomeMessage compact onConnectZoom={handleConnectZoom} onSendMessage={handleSendMessage} onNavigate={navigate} />
              </div>
              <div className="px-3 py-3">
                {chatInput}
              </div>
            </div>
          </div>
          {showAccountManager && (
            <AccountManager
              onClose={() => setShowAccountManager(false)}
              onAddAccount={handleConnectZoom}
            />
          )}
        </>
      );
    }

    // Default: full chat, no browser
    return (
      <>
        <div className="flex flex-col h-full">
          <StatusBar />
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="mx-auto max-w-3xl w-full px-4 pt-4">
              <WelcomeMessage onConnectZoom={handleConnectZoom} onSendMessage={handleSendMessage} onNavigate={navigate} />
            </div>
          </div>
          <div className="mx-auto max-w-3xl w-full px-4 py-4">
            {chatInput}
          </div>
        </div>
        {showAccountManager && (
          <AccountManager
            onClose={() => setShowAccountManager(false)}
            onAddAccount={handleConnectZoom}
          />
        )}
      </>
    );
  }

  // =========================================================================
  // Active conversation
  // =========================================================================

  const chatContent = (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      <StatusBar showZoomChip={browserOpen} />

      {/* Guided-setup progress — pinned above the transcript so the user always
          knows where they are and what's left. */}
      {inSetupChat && <SetupStepper />}

      {/* Write-access enablement (redirected here from a report Fix) — its own
          guided mini-flow with the browser panel visible. */}
      {inSetupChat && writeEnablePhase !== 'idle' && <WriteEnablePanel />}

      {/* Message list — min-h-0 lets this flex child shrink below its content
          height so ONLY this region scrolls (not the whole sidebar/page). */}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto">
        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          streamingContent={streamingContent}
          streamingArtifacts={streamingArtifacts}
          profilingProgress={profilingProgress}
          thinkingStatus={thinkingStatus}
        />
      </div>

      {/* Input area */}
      <div className={browserOpen ? 'px-3 py-3' : 'mx-auto max-w-3xl w-full px-4 py-4'}>
        {chatInput}
      </div>
    </div>
  );

  // Browser mode: browser fills main area, chat in right sidebar
  if (browserOpen) {
    return (
      <>
        <div className="flex h-full">
          {/* Browser fills main area */}
          <div className="flex-1 min-w-0">
            <BrowserPanel onClose={closeBrowserPanel} />
          </div>

          {/* Chat sidebar */}
          <div
            className="hidden lg:flex flex-col border-l border-border bg-background"
            style={{ width: CHAT_SIDEBAR_WIDTH, minWidth: CHAT_SIDEBAR_WIDTH }}
          >
            {chatContent}
          </div>
        </div>
        {showAccountManager && (
          <AccountManager
            onClose={() => setShowAccountManager(false)}
            onAddAccount={handleConnectZoom}
          />
        )}
      </>
    );
  }

  // Default: full chat, no browser
  return (
    <>
      <div className="flex flex-col h-full">
        {chatContent}
      </div>
      {showAccountManager && (
        <AccountManager
          onClose={() => setShowAccountManager(false)}
          onAddAccount={handleConnectZoom}
        />
      )}
    </>
  );
}
